import { useState } from 'react';
import Modal from '@/components/base/Modal';
import Button from '@/components/base/Button';
import { useToast } from '@/components/feature/ToastProvider';
import { BRAND_FOOTER, BRAND_NAME, LOGO_URL } from '@/lib/brand';
import { downloadReceiptPdf, printReceiptPdf, toReceiptPdfData } from '@/lib/receiptPdf';
import { formatCurrencyPlain, formatDate } from '@/lib/format';
import type { Receipt } from '@/types';

interface ReceiptPreviewProps {
  receipt: Receipt | null;
  onClose: () => void;
}

export default function ReceiptPreview({ receipt, onClose }: ReceiptPreviewProps) {
  const { notify } = useToast();
  const [busy, setBusy] = useState(false);

  if (!receipt) return null;

  const data = toReceiptPdfData(receipt, receipt.created_by_name || 'Usuario');

  const handleDownload = async () => {
    setBusy(true);
    try {
      await downloadReceiptPdf(data);
      notify({ tone: 'success', title: 'Recibo descargado', message: `${data.receiptNumber}.pdf generado.` });
    } catch {
      notify({ tone: 'error', title: 'No se pudo descargar', message: 'Intente nuevamente.' });
    } finally {
      setBusy(false);
    }
  };

  const handlePrint = async () => {
    setBusy(true);
    try {
      await printReceiptPdf(data);
    } catch {
      notify({ tone: 'error', title: 'No se pudo imprimir', message: 'Intente nuevamente.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      title="Vista previa del recibo"
      description="Así se verá el comprobante antes de descargarlo o imprimirlo."
      icon="ri-file-list-3-line"
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Cerrar
          </Button>
          <Button variant="outline" icon="ri-printer-line" onClick={handlePrint} disabled={busy}>
            Imprimir
          </Button>
          <Button icon="ri-download-2-line" onClick={handleDownload} loading={busy}>
            Descargar PDF
          </Button>
        </>
      }
    >
      <div className="max-h-[62vh] overflow-y-auto rounded-md border border-background-200 bg-white p-5 text-foreground-900 md:p-6">
        <div className="flex items-start justify-between gap-4 border-b border-background-200 pb-4">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-md border border-background-200">
              <img src={LOGO_URL} alt={BRAND_NAME} className="h-full w-full object-cover" />
            </div>
            <div>
              <p className="font-heading text-base font-bold text-foreground-950">{BRAND_NAME}</p>
              <p className="text-[11px] text-foreground-500">Sistema de Seguimiento</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-heading text-xl font-extrabold tracking-wide text-foreground-950">
              RECIBO
            </p>
            <p className="text-xs font-semibold text-primary-700">N° {data.receiptNumber}</p>
            <p className="text-[11px] text-foreground-500">Fecha: {formatDate(data.date)}</p>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4 rounded-md bg-background-100 p-4 sm:grid-cols-2">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">
              Cliente
            </p>
            <p className="text-sm font-semibold text-foreground-900">{data.clientName}</p>
            <p className="mt-2 text-[10px] font-semibold uppercase tracking-wide text-foreground-400">
              Celular
            </p>
            <p className="text-sm text-foreground-800">{data.clientPhone}</p>
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">
              Proyecto
            </p>
            <p className="text-sm font-semibold text-foreground-900">{data.projectName}</p>
          </div>
        </div>

        <table className="mt-4 w-full border-collapse text-left text-sm">
          <thead>
            <tr className="bg-foreground-950 text-background-50">
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide">Concepto</th>
              <th className="px-3 py-2 text-xs font-semibold uppercase tracking-wide">Descripción</th>
              <th className="px-3 py-2 text-right text-xs font-semibold uppercase tracking-wide">
                Monto (Bs.)
              </th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-background-200">
              <td className="px-3 py-3 align-top font-medium">{data.concept}</td>
              <td className="px-3 py-3 align-top text-foreground-600">{data.description || '—'}</td>
              <td className="px-3 py-3 align-top text-right font-semibold">
                {formatCurrencyPlain(data.amount)}
              </td>
            </tr>
          </tbody>
        </table>

        <div className="mt-4 flex justify-end">
          <div className="flex items-center justify-between gap-6 rounded-md bg-primary-500 px-5 py-3 text-background-50 dark:text-foreground-950">
            <span className="text-sm font-semibold">TOTAL:</span>
            <span className="font-heading text-lg font-extrabold">
              Bs. {formatCurrencyPlain(data.amount)}
            </span>
          </div>
        </div>

        {data.observation && data.observation.trim() && (
          <div className="mt-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-foreground-400">
              Observación
            </p>
            <p className="mt-1 text-sm text-foreground-700">{data.observation}</p>
          </div>
        )}

        <div className="mt-10 grid grid-cols-1 gap-10 sm:grid-cols-2">
          <div className="flex flex-col items-center">
            <div className="w-full border-t border-foreground-400 pt-2 text-center">
              <p className="text-xs font-semibold text-foreground-700">Recibí conforme</p>
              <p className="text-[11px] text-foreground-500">Firma y aclaración</p>
            </div>
          </div>
          <div className="flex flex-col items-center">
            <div className="w-full border-t border-foreground-400 pt-2 text-center">
              <p className="text-xs font-semibold text-foreground-700">Entregué conforme</p>
              <p className="text-[11px] text-foreground-500">Firma y aclaración</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-2 border-t border-background-200 pt-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold text-foreground-800">
              Generado por: {data.generatedBy}
            </p>
            <p className="text-[11px] text-foreground-500">{BRAND_FOOTER}</p>
          </div>
          <p className="text-[11px] text-foreground-500">Emitido el {formatDate(new Date())}</p>
        </div>
      </div>
    </Modal>
  );
}